import { saveAs } from 'file-saver'

/**
 * Composable for downloading files
 * Uses file-saver library internally to handle file downloads
 *
 * @example
 * const { download } = useFileDownload()
 * download(blob, 'documento.pdf')
 */
export const useFileDownload = () => {
  /**
   * Download a file from a Blob
   * @param blob - The Blob data to download
   * @param filename - The name for the downloaded file
   */
  const download = (blob: Blob, filename: string): void => {
    saveAs(blob, filename)
  }

  /**
   * Download a file from a URL
   * @param url - The URL to download from
   * @param filename - The name for the downloaded file
   */
  const downloadFromUrl = async (url: string, filename: string): Promise<void> => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      saveAs(blob, filename)
    } catch (error) {
      console.error('Error downloading file:', error)
      throw error
    }
  }

  /**
   * Download a file from base64 string
   * @param base64 - The base64 string
   * @param filename - The name for the downloaded file
   * @param mimeType - The MIME type of the file (default: 'application/octet-stream')
   */
  const downloadFromBase64 = (base64: string, filename: string, mimeType = 'application/octet-stream'): void => {
    // Remove data URL prefix if present
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64

    // Convert base64 to blob
    const byteCharacters = atob(base64Data)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: mimeType })

    saveAs(blob, filename)
  }

  /**
   * Open a Blob in a new browser tab
   * Automatically manages URL creation and cleanup
   * @param blob - The Blob to open
   * @param mimeType - Optional MIME type (default: uses blob's type)
   */
  const openInNewTab = (blob: Blob, mimeType?: string): void => {
    // Create blob with specified mime type if provided
    const finalBlob = mimeType ? new Blob([blob], { type: mimeType }) : blob
    const url = URL.createObjectURL(finalBlob)

    // Open in new tab
    const newWindow = window.open(url, '_blank')

    // Cleanup: revoke URL after window loads or after timeout
    if (newWindow) {
      newWindow.onload = () => {
        setTimeout(() => URL.revokeObjectURL(url), 100)
      }
    } else {
      // Fallback if popup was blocked
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    }
  }

  /**
   * Convert Blob to base64 data URL
   * @param blob - The Blob to convert
   * @returns Promise with base64 data URL (includes data:mime/type;base64, prefix)
   */
  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  /**
   * Convert Blob to base64 string (without data URL prefix)
   * @param blob - The Blob to convert
   * @returns Promise with pure base64 string
   */
  const blobToBase64String = async (blob: Blob): Promise<string> => {
    const dataUrl = await blobToBase64(blob)
    return dataUrl.split(',')[1]
  }

  /**
   * Convert Blob to text
   * @param blob - The Blob to convert
   * @returns Promise with text content
   */
  const blobToText = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsText(blob)
    })
  }

  const fileToBase64 = (files: any[], fieldName: string, fileName: string): Promise<any[]> => {
    return Promise.all(files.map((file: any) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          const data64 = String(reader.result).replace(/^data:.+;base64,/, '')
          resolve({ [fieldName]: data64, [fileName]: file.file.name })
        }
        reader.onerror = reject
        reader.readAsDataURL(file.file)
      })
    }))
  }

  return {
    download,
    downloadFromUrl,
    downloadFromBase64,
    openInNewTab,
    blobToBase64,
    blobToBase64String,
    blobToText,
    fileToBase64
  }
}
